import { faChevronDown, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMaintenanceDue } from 'src/api/maintenance';
import { getRouteURI } from 'src/utils/url';
import { getUuid } from 'src/utils/url';

import StatusBadge from '../ui/StatusBadge';
import MaintenanceStatusBadge from './MaintenanceStatusBadge';

const badgeConfig = {
  attention: {
    badgeText: 'Service Now',
    badgeVariant: 'error',
  },
  healthy: {
    badgeText: 'Maintained',
    badgeVariant: 'success',
  },
};

const defaultCount = {
  overdue: 0,
  warning: 0,
  healthy: 0,
};

const MaintenanceHealthBadge = ({
  equipmentUri,
  className,
  linkClassName,
  isBinary,
  maintenanceRoute,
}) => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [statusCount, setStatusCount] = useState(defaultCount);

  useEffect(() => {
    if (!equipmentUri) {
      setConfig(null);
      setStatusCount(defaultCount);
      return;
    }

    setLoading(true);

    const loadMaintenanceDue = async () => {
      setStatusCount(defaultCount);

      try {
        const maintenanceDue = await getMaintenanceDue(equipmentUri);
        if (maintenanceDue.length === 0) {
          setConfig(null);
          setStatusCount(defaultCount);
          return;
        }

        // eslint-disable-next-line camelcase
        const needsService = maintenanceDue.some(({ status_indicator }) =>
          ['overdue'].includes(status_indicator)
        );

        setConfig(needsService ? badgeConfig.attention : badgeConfig.healthy);

        const counts = maintenanceDue.reduce(
          (acc, { status_indicator: status }) => {
            if (status in acc) {
              acc[status] += 1;
            }

            return acc;
          },
          {
            overdue: 0,
            warning: 0,
            healthy: 0,
          }
        );

        setStatusCount(counts);
      } catch {
        setConfig(null);
        setStatusCount(defaultCount);
      } finally {
        setLoading(false);
      }
    };

    loadMaintenanceDue();
  }, [equipmentUri]);

  const handleToggle = () => {
    setShowDetails(current => !current);
  };

  if (!config) {
    return null;
  }

  return (
    <div
      style={{ visibility: loading && !config ? 'hidden' : 'visible' }}
      className='d-flex flex-column align-items-center'
      onClick={event => event.stopPropagation()}
    >
      {isBinary && (
        <StatusBadge
          variant={config?.badgeVariant ?? badgeConfig.healthy.badgeVariant}
          className={`${className} status-badge--button`}
          onClick={handleToggle}
        >
          {config?.badgeText ?? badgeConfig.healthy.badgeText}
          <FontAwesomeIcon icon={faChevronDown} rotation={showDetails ? 180 : undefined} />
        </StatusBadge>
      )}
      {(!isBinary || showDetails) && (
        <>
          <div className='d-flex flex-wrap align-items-center gap-5 mt-2'>
            <MaintenanceStatusBadge status='overdue' count={statusCount.overdue} />
            <MaintenanceStatusBadge status='warning' count={statusCount.warning} />
            <MaintenanceStatusBadge status='healthy' count={statusCount.healthy} />
          </div>
          {showDetails && maintenanceRoute && (
            <Link
              to={getRouteURI(maintenanceRoute, { uuid: getUuid(equipmentUri) })}
              className={`mt-2 fw-semibold ${linkClassName}`}
              style={{ fontSize: '16px' }}
            >
              <FontAwesomeIcon icon={faScrewdriverWrench} className='mr-1' size='sm' />
              View Maintenance
            </Link>
          )}
        </>
      )}
    </div>
  );
};

MaintenanceHealthBadge.defaultProps = {
  isBinary: true,
};

MaintenanceHealthBadge.propTypes = {
  equipmentUri: PropTypes.string.isRequired,
  className: PropTypes.string,
  linkClassName: PropTypes.string,
  isBinary: PropTypes.bool,
  maintenanceRoute: PropTypes.string,
};

export default MaintenanceHealthBadge;
